import { createReducer, on } from '@ngrx/store';
import {
  OpenTeamChatThreadAction,
  CloseTeamChatThreadAction,
  FetchTeamRecentMessagesAction,
  FetchTeamRecentMessagesFailAction,
  FetchTeamRecentMessagesSuccessAction,
  FetchTeamChatMessagesSuccessAction,
  FetchTeamChatMessagesAction,
  SendTeamChatMessageAction,
  ClearPendingTeamChatMessageAction,
  FetchTeamChatMessagesFailAction,
  DeleteTeamChatMessageSuccessAction,
  CloseAllTeamChatThreadsAction,
  SendTeamChatMessageSuccessAction,
  SendTeamChatMessageFailAction,
  ResendTeamChatMessageAction,
  AddSocketTeamChatMessageAction,
  ExpandMessageWindowAction,
  ExpandMessageWindowSuccessAction,
  ExpandMessageWindowFailAction,
  CleanupConversationMemoryAction,
} from './teamInbox.action';
import { InteractionDirection, InteractionStatus, InteractionThread, InteractionType, RecentInteraction } from '@/app/core/Models/Interaction';
import { SignOutAction } from '../app/action';
import { TeamMember } from '@/app/core/Models/TeamMember';
import { TeamInteraction } from '@/app/core/Models/interaction-team';
import { TeamMessageBody } from '@/app/Data/DTO/TeamChatMessageDto';

// Helper function to process socket messages consistently
function processSocketMessage(state: TeamInboxState, interaction: InteractionThread): TeamInboxState {
  // Add new message from socket, deduplicate to prevent duplicates
  const existingIndex = state.threadInteractions.nextPagesThreads.findIndex(i => i.interaction_id === interaction.interaction_id);
  
  let nextPagesThreads;
  if (existingIndex === -1) {
    // New message, add it to the list
    nextPagesThreads = [interaction, ...state.threadInteractions.nextPagesThreads];
  } else {
    // Message already exists, update it (in case of status changes)
    nextPagesThreads = [...state.threadInteractions.nextPagesThreads];
    nextPagesThreads[existingIndex] = interaction;
  }

  // Respect current window size limits to prevent memory bloat
  const currentWindowSize = state.threadInteractions.windowSize || 100;
  const sortedThreads = nextPagesThreads
    .sort((a, b) => b.interaction_dt.getTime() - a.interaction_dt.getTime())
    .slice(0, currentWindowSize); // Enforce window size limit
  
  return {
    ...state,
    threadInteractions: {
      ...state.threadInteractions,
      nextPagesThreads: sortedThreads
    },
  };
}

// Helper function to process queued socket messages with proper ordering
function processQueuedMessages(state: TeamInboxState): TeamInboxState {
  const pendingMessages = state.threadInteractions.pendingSocketMessages || [];
  if (pendingMessages.length === 0) {
    return state;
  }

  // Safety check: don't process if operation lock is already active
  if (state.threadInteractions.operationLock) {
    console.log('Cannot process queued messages: operation lock is active');
    return state;
  }

  console.log(`Processing ${pendingMessages.length} queued socket messages`);
  
  // Sort messages by interaction_dt to ensure chronological order
  const sortedMessages = [...pendingMessages].sort((a, b) => {
    const timeA = new Date(a.interaction_dt).getTime();
    const timeB = new Date(b.interaction_dt).getTime();
    return timeA - timeB; // Ascending order (oldest first)
  });

  // Clear queue first to prevent reprocessing and set operation lock
  let currentState: TeamInboxState = {
    ...state,
    threadInteractions: {
      ...state.threadInteractions,
      pendingSocketMessages: [] as InteractionThread[], // Clear the queue with proper typing
      operationLock: true, // Lock to prevent concurrent modifications
      queuedMessageCount: 0
    }
  };

  // Track processing metrics
  let processedCount = 0;
  let skippedCount = 0;

  // Process messages one by one in chronological order to maintain data integrity
  for (const message of sortedMessages) {
    // Additional validation: ensure message isn't duplicate of existing messages
    const existingIndex = currentState.threadInteractions.nextPagesThreads.findIndex(
      existing => existing.interaction_id === message.interaction_id
    );
    
    if (existingIndex === -1) {
      // Only process if not already present
      currentState = processSocketMessage(currentState, message);
      processedCount++;
    } else {
      console.log(`Skipping duplicate queued message: ${message.interaction_id}`);
      skippedCount++;
    }
  }

  // Update processing tracking and release operation lock
  const latestMessageTime = sortedMessages.length > 0 
    ? Math.max(...sortedMessages.map(m => new Date(m.interaction_dt).getTime()))
    : currentState.threadInteractions.lastProcessedMessageTime || Date.now();

  // Final state with updated tracking
  currentState = {
    ...currentState,
    threadInteractions: {
      ...currentState.threadInteractions,
      operationLock: false, // Release the lock
      lastProcessedMessageTime: latestMessageTime
    }
  };

  console.log(`Queue processing complete: ${processedCount} processed, ${skippedCount} skipped`);

  return currentState;
}

export interface TeamInteractionsState<T> {
  fetching: boolean;
  isLastPage: boolean;
  page: number;
  nextPagesThreads: T[],
  error?: string,
  // Memory management fields
  windowSize?: number;
  lastCleanupTime?: number;
  conversationMemoryMap?: { [userId: string]: { messages: T[], lastAccessed: number } };
  // Error handling and loading states
  expandingWindow?: boolean;
  expandError?: string;
  lastExpandPage?: number;
  // Race condition handling
  pendingSocketMessages?: T[];
  operationLock?: boolean;
  // Message sequence tracking
  lastProcessedMessageTime?: number;
  queuedMessageCount?: number;
}


export interface TeamInboxState {
  focusedUser: number;
  activeUsers: TeamMember[];
  recentInteractions: TeamInteractionsState<RecentInteraction>;
  threadInteractions: TeamInteractionsState<InteractionThread>;
  pendingThreadInteractions: { interaction: InteractionThread, body: TeamMessageBody }[];
  sending?: boolean;
}

export const initTeamInboxState: TeamInboxState = {
  focusedUser: 0,
  activeUsers: [],
  recentInteractions: {
    page: 1,
    fetching: false,
    isLastPage: false,
    nextPagesThreads: []
  },
  threadInteractions: {
    page: 1,
    fetching: false,
    isLastPage: false,
    nextPagesThreads: [],
    windowSize: 100, // Start with 100 messages
    lastCleanupTime: Date.now(),
    conversationMemoryMap: {},
    expandingWindow: false,
    expandError: undefined,
    lastExpandPage: 0,
    pendingSocketMessages: [],
    operationLock: false,
    lastProcessedMessageTime: Date.now(),
    queuedMessageCount: 0
  },
  pendingThreadInteractions: [],
  sending: false
};

export const teamInboxReducer = createReducer(initTeamInboxState,
  // new message ---------------------------------------------------------------
  on(SendTeamChatMessageAction, (state: TeamInboxState, { payload }) => {
    let body = payload.getBody()
    let pendingTeamInteraction = Object.assign<TeamInteraction, Partial<TeamInteraction>>(new TeamInteraction(), {
      created_dt: new Date().toUTCString(),
      attachments: body.attachments.map(({ type, url, name }) => ({ type, url, name })),
      data: body.data,
      direction: InteractionDirection.outbound,
      status: InteractionStatus.sending,
      type: InteractionType.message,
      interaction_id: 0,
      is_room: false,
      receiver_id: body.receiver_id,
      sender_id: body.user_id,
      msg_id: body.msg_id
    });
    return {
      ...state,
      sending: true,
      pendingThreadInteractions: [
        ...state.pendingThreadInteractions,
        {
          interaction: pendingTeamInteraction.mapToThreadInteraction(),
          body: payload
        }]
    };
  }),
  on(SendTeamChatMessageSuccessAction, (state: TeamInboxState, { payload }) => {
    return {
      ...state,
      sending: false,
    };
  }),
  on(SendTeamChatMessageFailAction, (state: TeamInboxState, { payload: { msg_id } }) => {
    const { body, interaction } = state.pendingThreadInteractions.find(x => x.body.msg_id === msg_id)!
    return {
      ...state,
      sending: false,
      pendingThreadInteractions: [
        ...state.pendingThreadInteractions.filter(x => x.body.msg_id !== msg_id),
        {
          body,
          interaction: Object.assign<InteractionThread, Partial<InteractionThread>>(
            new InteractionThread(),
            { ...interaction, status: InteractionStatus.failed }
          )
        }
      ]
    };
  }),
  on(ResendTeamChatMessageAction, (state: TeamInboxState, { payload: { msg_id } }) => {
    const { body, interaction } = state.pendingThreadInteractions.find(x => x.body.msg_id === msg_id)!
    return {
      ...state,
      sending: true,
      pendingThreadInteractions: [
        ...state.pendingThreadInteractions.filter(x => x.body.msg_id !== msg_id),
        {
          body,
          interaction: Object.assign<InteractionThread, Partial<InteractionThread>>(
            new InteractionThread(),
            { ...interaction, status: InteractionStatus.sending }
          )
        }
      ]
    };
  }),
  on(ClearPendingTeamChatMessageAction, (state: TeamInboxState, { payload }) => {
    return {
      ...state,
      pendingThreadInteractions: state.pendingThreadInteractions.filter(m => m.body.msg_id && m.body.msg_id !== payload.msg_id)
    };
  }),
  // recent messages ---------------------------------------------------------------
  on(FetchTeamRecentMessagesAction, (state: TeamInboxState, { payload }) => {
    return {
      ...state,
      recentInteractions: {
        ...state.recentInteractions,
        page: payload.page,
        fetching: true,
      },
    };
  }),
  on(FetchTeamRecentMessagesSuccessAction, (state: TeamInboxState, { payload }) => {
    return {
      ...state,
      recentInteractions: {
        ...state.recentInteractions,
        fetching: false,
        isLastPage: !payload?.length,
      },
    };
  }),
  on(FetchTeamRecentMessagesFailAction, (state: TeamInboxState, { payload }) => {
    return {
      ...state,
      recentInteractions: {
        ...state.recentInteractions,
        fetching: false,
      },
    };
  }),

  // thread messages -------------------------------------------------------------
  on(FetchTeamChatMessagesAction, (state: TeamInboxState, { payload: { page, userId, page_size } }) => {
    return {
      ...state,
      threadInteractions: {
        ...state?.threadInteractions,
        fetching: true,
        error: undefined,
      },
    };
  }),
  on(FetchTeamChatMessagesSuccessAction, (state: TeamInboxState, { payload: { interactions, page } }) => {
    // Handle race condition: if we're in the middle of cleanup, wait
    if (state.threadInteractions.lastCleanupTime && 
        Date.now() - state.threadInteractions.lastCleanupTime < 1000) { // Within 1 second of cleanup
      console.log('Fetch completed shortly after cleanup, applying carefully');
    }

    // Remove existing messages for this page to avoid duplicates
    let nextPagesThreads = state.threadInteractions.nextPagesThreads.filter(i => i.page != page)
    
    // Add new interactions for this page
    nextPagesThreads = [...interactions, ...nextPagesThreads]
    
    // Deduplicate by interaction_id to prevent duplicates from socket/API race conditions
    const uniqueThreads = nextPagesThreads.reduce((acc: InteractionThread[], interaction: InteractionThread) => {
      const existingIndex = acc.findIndex((i: InteractionThread) => i.interaction_id === interaction.interaction_id);
      if (existingIndex === -1) {
        acc.push(interaction);
      } else {
        // Keep the newer version (from API response typically has more complete data)
        acc[existingIndex] = interaction;
      }
      return acc;
    }, [] as InteractionThread[]);
    
    // Sort by interaction date
    const sortedThreads = uniqueThreads.sort((a: InteractionThread, b: InteractionThread) => b.interaction_dt.getTime() - a.interaction_dt.getTime());

    // Enforce window size limits after fetching
    const windowSize = state.threadInteractions.windowSize || 100;
    const trimmedThreads = sortedThreads.slice(0, windowSize);
    
    // Clear fetching state and process any queued messages
    const clearedState = {
      ...state,
      threadInteractions: {
        ...state.threadInteractions,
        page,
        fetching: false,
        isLastPage: !interactions?.length,
        nextPagesThreads: trimmedThreads // Keep all pages but respect window size
      },
    };

    // Process any queued socket messages that arrived during fetch
    return processQueuedMessages(clearedState);
  }),
  on(AddSocketTeamChatMessageAction, (state: TeamInboxState, { payload: { interaction } }) => {
    // Check if we're currently in a critical operation to avoid race conditions
    const isInCriticalOperation = state.threadInteractions.expandingWindow || 
                                 state.threadInteractions.operationLock ||
                                 state.threadInteractions.fetching;
    
    // Also check if cleanup happened very recently (within 500ms)
    const recentCleanup = state.threadInteractions.lastCleanupTime && 
                         (Date.now() - state.threadInteractions.lastCleanupTime) < 500;

    if (isInCriticalOperation || recentCleanup) {
      console.log('Socket message received during critical operation or recent cleanup, queuing for later');
      
      // Queue the message for later processing with sequence validation
      const pendingMessages = state.threadInteractions.pendingSocketMessages || [];
      
      // Ensure message is for the same conversation to prevent mixing
      const focusedUser = state.focusedUser;
      if (focusedUser && interaction.user_id == focusedUser.toString()) {
        // Validate message isn't too old (prevent processing very stale messages)
        const messageTime = new Date(interaction.interaction_dt).getTime();
        const currentTime = Date.now();
        const maxAgeMs = 5 * 60 * 1000; // 5 minutes max age
        
        if (currentTime - messageTime > maxAgeMs) {
          console.log(`Ignoring very old socket message (age: ${Math.round((currentTime - messageTime) / 1000)}s)`);
          return state;
        }

        // Check if message is already queued (prevent duplicate queuing)
        const alreadyQueued = pendingMessages.some(queued => queued.interaction_id === interaction.interaction_id);
        if (alreadyQueued) {
          console.log(`Message ${interaction.interaction_id} already queued, skipping`);
          return state;
        }

        // Limit queue size to prevent memory issues
        const maxQueueSize = 50;
        const newQueue = pendingMessages.length >= maxQueueSize 
          ? [...pendingMessages.slice(1), interaction] // Remove oldest if at limit
          : [...pendingMessages, interaction];

        return {
          ...state,
          threadInteractions: {
            ...state.threadInteractions,
            pendingSocketMessages: newQueue,
            queuedMessageCount: newQueue.length
          }
        };
      } else {
        // Different conversation, ignore during critical operations
        console.log('Socket message for different conversation during critical operation, ignoring');
        return state;
      }
    }

    // Process the message immediately
    return processSocketMessage(state, interaction);
  }),
  on(ExpandMessageWindowAction, (state: TeamInboxState, { payload: { userId, additionalSize, page } }) => {
    const currentWindowSize = state.threadInteractions.windowSize || 100;
    const newWindowSize = currentWindowSize + additionalSize;
    
    return {
      ...state,
      threadInteractions: {
        ...state.threadInteractions,
        windowSize: Math.min(newWindowSize, 1000), // Cap at 1000 messages max
        expandingWindow: true, // Set loading state
        expandError: undefined, // Clear any previous errors
        lastExpandPage: page || state.threadInteractions.page + 1
      },
    };
  }),
  on(ExpandMessageWindowSuccessAction, (state: TeamInboxState, { payload: { userId, page } }) => {
    // Clear expansion state and process any queued messages
    const clearedState = {
      ...state,
      threadInteractions: {
        ...state.threadInteractions,
        expandingWindow: false, // Clear loading state
        expandError: undefined, // Clear any errors
        page: Math.max(page, state.threadInteractions.page) // Update to latest page
      },
    };

    // Process any queued socket messages that arrived during expansion
    return processQueuedMessages(clearedState);
  }),
  on(ExpandMessageWindowFailAction, (state: TeamInboxState, { payload: { userId, error } }) => {
    // Revert window size expansion on error
    const revertedWindowSize = Math.max((state.threadInteractions.windowSize || 100) - 50, 100);
    
    return {
      ...state,
      threadInteractions: {
        ...state.threadInteractions,
        expandingWindow: false, // Clear loading state
        expandError: error, // Set error message
        windowSize: revertedWindowSize // Revert the expansion
      },
    };
  }),
  on(CleanupConversationMemoryAction, (state: TeamInboxState, { payload: { userId, maxAge } }) => {
    const maxAgeMs = maxAge || (30 * 60 * 1000); // Default 30 minutes
    const now = Date.now();
    
    // Clean up old conversations from memory map
    const cleanedMemoryMap = { ...state.threadInteractions.conversationMemoryMap };
    Object.keys(cleanedMemoryMap).forEach(key => {
      const conversation = cleanedMemoryMap[key];
      if (userId && key !== userId.toString()) {
        return; // Only clean specific user if provided
      }
      
      if (now - conversation.lastAccessed > maxAgeMs) {
        delete cleanedMemoryMap[key];
      }
    });
    
    // If cleaning current conversation, also reduce nextPagesThreads
    let nextPagesThreads = state.threadInteractions.nextPagesThreads;
    if (userId && userId.toString() === state.focusedUser?.toString()) {
      // Keep only recent messages for current conversation
      nextPagesThreads = nextPagesThreads
        .sort((a, b) => b.interaction_dt.getTime() - a.interaction_dt.getTime())
        .slice(0, 100);
    }
    
    return {
      ...state,
      threadInteractions: {
        ...state.threadInteractions,
        nextPagesThreads,
        lastCleanupTime: now,
        conversationMemoryMap: cleanedMemoryMap,
        windowSize: userId && userId.toString() === state.focusedUser?.toString() ? 100 : state.threadInteractions.windowSize
      },
    };
  }),
  on(CloseTeamChatThreadAction, (state: TeamInboxState, { payload: { userId } }) => {
    // Trigger cleanup when closing a conversation
    const cleanedMemoryMap = { ...state.threadInteractions.conversationMemoryMap };
    delete cleanedMemoryMap[userId.toString()];
    
    return {
      ...state,
      threadInteractions: {
        ...state.threadInteractions,
        conversationMemoryMap: cleanedMemoryMap,
        // Reset window size if closing current conversation
        windowSize: userId === state.focusedUser ? 100 : state.threadInteractions.windowSize
      }
    };
  }),
  on(DeleteTeamChatMessageSuccessAction, (state: TeamInboxState, { payload: { threadInteraction } }) => {
    let nextPagesThreads = state.threadInteractions.nextPagesThreads.filter(i => i.interaction_id != threadInteraction.interaction_id)
    return {
      ...state,
      threadInteractions: {
        ...state.threadInteractions,
        nextPagesThreads
      },
    };
  }),
  on(FetchTeamChatMessagesFailAction, (state: TeamInboxState, { payload }) => {
    return {
      ...state,
      threadInteractions: {
        ...state.threadInteractions,
        fetching: false,
        error: payload
      },
    };
  }),
  // -----------------------------------------------------------------------------
  on(OpenTeamChatThreadAction, (state: TeamInboxState, { payload: { user, idle } }) => {
    if (user) {
      let activeUsers = state.activeUsers.find(u => u.user_id == user.user_id)
        ? [...state.activeUsers]
        : [...state.activeUsers, user]
      activeUsers.length > 3 && activeUsers.shift()
      return {
        ...state,
        focusedUser: !idle ? user.user_id : (state.focusedUser || user.user_id),
        activeUsers
      };
    }

    return state
  }),
  on(CloseTeamChatThreadAction, (state: TeamInboxState, { payload: { userId } }) => {
    return {
      ...state,
      focusedUser: state.focusedUser == userId ? 0 : state.focusedUser,
      activeUsers: state.activeUsers.filter(u => u.user_id !== userId),
      threadInteractions: {
        page: 1,
        fetching: false,
        isLastPage: false,
        nextPagesThreads: []
      },
      pendingThreadInteractions: []
    };
  }),
  on(CloseAllTeamChatThreadsAction, (state: TeamInboxState,) => {
    return {
      ...state,
      focusedUser: 0,
      activeUsers: [],
      threadInteractions: {
        page: 1,
        fetching: false,
        isLastPage: false,
        nextPagesThreads: []
      },
      pendingThreadInteractions: []
    };
  }),
  on(SignOutAction, (state: TeamInboxState) => {
    return {
      ...initTeamInboxState,
    };
  })
);
